/**
 * Search helpers for the typeahead (multiselect) fields that pick an item out
 * of a long "CODE DESCRIPTION" list, e.g. "185 MALIGNANT NEOPLASM OF PROSTATE".
 *
 * vue-multiselect filters such a list by plain substring match and keeps the
 * original order, so a short code that is contained in many longer codes
 * (searching "185" also matches "1850", "18501", and any description mentioning
 * 185) gets pushed out of the visible part of the dropdown and cannot be
 * selected at all. Ranking the matches keeps the searched code on top, also
 * when the same code has several descriptions.
 */

// Number of options shown when no search was typed yet
export const DEFAULT_OPTIONS_LIMIT = 10;
// Number of options shown while searching
export const SEARCH_OPTIONS_LIMIT = 100;

// Ranks of a search result, lower is shown higher in the dropdown
const RANK_EXACT_CODE = 0;
const RANK_CODE_PREFIX = 1;
const RANK_WORD_PREFIX = 2;
const RANK_CONTAINS = 3;
const RANK_NO_MATCH = 4;

/**
 * Extracts the leading code of an option,
 * e.g. "185 MALIGNANT NEOPLASM OF PROSTATE" -> "185", "701 > RHINOPLASTY" -> "701"
 */
function getOptionCode(option) {
  const text = String(option).trim();
  const spaceIndex = text.indexOf(" ");
  return (spaceIndex === -1 ? text : text.slice(0, spaceIndex)).toLowerCase();
}

/**
 * Scores how well an option matches the search tokens.
 * All tokens must appear somewhere in the option, so "185 prostate" still
 * matches, while the leading code decides the ordering.
 */
function getMatchRank(option, tokens) {
  const text = String(option).toLowerCase();
  if (!tokens.every(token => text.includes(token))) return RANK_NO_MATCH;

  const [firstToken] = tokens;
  const code = getOptionCode(option);
  if (code === firstToken) return RANK_EXACT_CODE;
  if (code.startsWith(firstToken)) return RANK_CODE_PREFIX;

  const words = text.split(/[\s,./()[\]-]+/);
  if (words.some(word => word.startsWith(firstToken))) return RANK_WORD_PREFIX;

  return RANK_CONTAINS;
}

/**
 * Filters options by the search text and orders them by relevance.
 * Options of equal relevance keep their original order, so the doctor's
 * history items stay above the rest of the list.
 *
 * Requires `:internal-search="false"` on the multiselect, otherwise the
 * library filters the result again by its own rules.
 *
 * @param {Array} options - full list of options
 * @param {String} searchQuery - text typed in the multiselect
 * @returns {Array} matching options, most relevant first
 */
export function filterTypeheadOptions(options, searchQuery) {
  const list = Array.isArray(options) ? options : [];
  const tokens = String(searchQuery || "")
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!tokens.length) return list;

  return list
    .map((option, index) => ({
      option,
      index,
      rank: getMatchRank(option, tokens)
    }))
    .filter(item => item.rank !== RANK_NO_MATCH)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(item => item.option);
}
